/**
 * Frontend JWT management service.
 * Stores APP-plane and PLATFORM-plane tokens in separate localStorage keys.
 */

const APP_TOKEN_KEY = 'tp_auth_token';
const PLATFORM_TOKEN_KEY = 'tp_platform_admin_token';
const SESSION_EXPIRED_EVENT = 'tp:session-expired';
const TOKEN_CLEARED_EVENT = 'tp:token-cleared';

/** Minimal subset of JWT payload needed by the client. */
export interface LocalJwtClaims {
  sub: string;
  email: string;
  role: string | null;
  customRoleId: string | null;
  permissions: Record<string, boolean>;
  createdById: string | null;
  tenantProfileId: string | null;
  accountStatus: string;
  mustChangePassword: boolean;
  mustVerifyPhone: boolean;
  aud?: string;
  exp: number;
  iat: number;
}

function decodeJwt(token: string): LocalJwtClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as LocalJwtClaims;
  } catch {
    return null;
  }
}

function isExpired(claims: LocalJwtClaims): boolean {
  return Date.now() / 1000 >= claims.exp;
}

export function isPlatformAdminClaims(claims: LocalJwtClaims | null | undefined): boolean {
  if (!claims) return false;
  if (claims.aud === 'tenantplus-platform-admin') return true;
  if (claims.role === 'PLATFORM_ADMIN') return true;
  // Legacy: ADMIN + createdById null
  if (claims.role === 'ADMIN' && (claims.createdById == null || claims.createdById === undefined)) {
    return true;
  }
  return false;
}

class AuthTokenService {
  private _appToken: string | null = null;
  private _appClaims: LocalJwtClaims | null = null;
  private _platformToken: string | null = null;
  private _platformClaims: LocalJwtClaims | null = null;

  constructor() {
    this._load();
  }

  private _load() {
    try {
      const appStored = localStorage.getItem(APP_TOKEN_KEY);
      if (appStored) {
        const claims = decodeJwt(appStored);
        if (claims && !isExpired(claims) && !isPlatformAdminClaims(claims)) {
          this._appToken = appStored;
          this._appClaims = claims;
        } else if (claims && isPlatformAdminClaims(claims) && !isExpired(claims)) {
          // Migrate misplaced platform token out of app key
          this._platformToken = appStored;
          this._platformClaims = claims;
          localStorage.setItem(PLATFORM_TOKEN_KEY, appStored);
          localStorage.removeItem(APP_TOKEN_KEY);
        } else {
          localStorage.removeItem(APP_TOKEN_KEY);
        }
      }

      const platformStored = localStorage.getItem(PLATFORM_TOKEN_KEY);
      if (platformStored) {
        const claims = decodeJwt(platformStored);
        if (claims && !isExpired(claims) && isPlatformAdminClaims(claims)) {
          this._platformToken = platformStored;
          this._platformClaims = claims;
        } else {
          localStorage.removeItem(PLATFORM_TOKEN_KEY);
        }
      }
    } catch {
      // localStorage unavailable
    }
  }

  private _clearApp() {
    this._appToken = null;
    this._appClaims = null;
    try {
      localStorage.removeItem(APP_TOKEN_KEY);
    } catch {
      /* */
    }
  }

  private _clearPlatform() {
    this._platformToken = null;
    this._platformClaims = null;
    try {
      localStorage.removeItem(PLATFORM_TOKEN_KEY);
    } catch {
      /* */
    }
  }

  private _clearAll() {
    this._clearApp();
    this._clearPlatform();
  }

  /**
   * Persist a newly-issued token into the correct plane key.
   * Logging into one plane clears the other (single active session).
   */
  setToken(token: string): LocalJwtClaims | null {
    const claims = decodeJwt(token);
    if (!claims || isExpired(claims)) return null;

    if (isPlatformAdminClaims(claims)) {
      this._clearApp();
      this._platformToken = token;
      this._platformClaims = claims;
      try {
        localStorage.setItem(PLATFORM_TOKEN_KEY, token);
      } catch {
        /* */
      }
      return claims;
    }

    this._clearPlatform();
    this._appToken = token;
    this._appClaims = claims;
    try {
      localStorage.setItem(APP_TOKEN_KEY, token);
    } catch {
      /* */
    }
    return claims;
  }

  /** Active session token (platform preferred if present). */
  getToken(): string | null {
    const platform = this.getPlatformToken();
    if (platform) return platform;
    return this.getAppToken();
  }

  getAppToken(): string | null {
    if (!this._appToken || !this._appClaims) return null;
    if (isExpired(this._appClaims)) {
      this._clearApp();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      return null;
    }
    return this._appToken;
  }

  getPlatformToken(): string | null {
    if (!this._platformToken || !this._platformClaims) return null;
    if (isExpired(this._platformClaims)) {
      this._clearPlatform();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      return null;
    }
    return this._platformToken;
  }

  /** Active session claims (platform preferred if present). */
  getClaims(): LocalJwtClaims | null {
    if (this._platformClaims && !isExpired(this._platformClaims)) return this._platformClaims;
    if (this._appClaims && !isExpired(this._appClaims)) return this._appClaims;
    return null;
  }

  getPlatformClaims(): LocalJwtClaims | null {
    if (!this._platformClaims || isExpired(this._platformClaims)) return null;
    return this._platformClaims;
  }

  /** Landlord / tenant SPA session claims only (never platform plane). */
  getAppClaims(): LocalJwtClaims | null {
    if (!this._appClaims || isExpired(this._appClaims)) return null;
    return this._appClaims;
  }

  /** Drop platform token without touching app session (landlord host hygiene). */
  clearPlatformToken(): void {
    this._clearPlatform();
  }

  clearToken(): void {
    this._clearAll();
    try {
      window.dispatchEvent(new CustomEvent(TOKEN_CLEARED_EVENT));
    } catch {
      /* */
    }
  }

  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  isPlatformSession(): boolean {
    return this.getPlatformToken() !== null;
  }

  getAuthHeader(): string | null {
    const t = this.getToken();
    return t ? `Bearer ${t}` : null;
  }

  getAppAuthHeader(): string | null {
    const t = this.getAppToken();
    return t ? `Bearer ${t}` : null;
  }

  getPlatformAuthHeader(): string | null {
    const t = this.getPlatformToken();
    return t ? `Bearer ${t}` : null;
  }

  /** Choose token plane based on API path or current UI route. */
  getAuthHeaderForUrl(url?: string): string | null {
    const path = url || '';
    const onAdminUi =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
    const isPlatformApi =
      path.includes('/platform-admin') ||
      /(^|\/)admin(\/|$)/.test(path) ||
      path.includes('/performance') ||
      path.includes('/test-alerts');

    if (onAdminUi || isPlatformApi) {
      return this.getPlatformAuthHeader() || this.getAuthHeader();
    }
    // Landlord SPA: never attach a leftover platform JWT to app APIs
    return this.getAppAuthHeader();
  }
}

export const authTokenService = new AuthTokenService();
export const SESSION_EXPIRED = SESSION_EXPIRED_EVENT;
export const TOKEN_CLEARED = TOKEN_CLEARED_EVENT;
